import { WorkfrontBundleState, WorkfrontBundleSyncStatus } from '@atlas/types'
import { CardShell, PaginationFooter, SectionCard } from '@atlas/ui-components'
import { Input, Label, Switch } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useWorkfrontBundles } from '@/hooks/useWorkfront'
import { BundlesTable } from './components/BundlesTable'
import { EnumFilter, PAGE_SIZE, WORKFRONT_PATHS, WorkfrontHeader } from './shared'

/** The bundles, filterable by state, sync status and open errors, searchable by title or reference number. */
export function WorkfrontBundlesPage() {
	const [state, setState] = useState<WorkfrontBundleState | undefined>()
	const [syncStatus, setSyncStatus] = useState<WorkfrontBundleSyncStatus | undefined>()
	const [withErrors, setWithErrors] = useState(false)
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const debouncedSearch = useDebouncedValue(search.trim(), 300)

	const query = useWorkfrontBundles({
		state,
		syncStatus,
		hasOpenErrors: withErrors ? true : undefined,
		search: debouncedSearch || undefined,
		page,
		limit: PAGE_SIZE,
	})
	const totalPages = query.data?.pagination.totalPages ?? 1
	const total = query.data?.pagination.total ?? 0

	// Any filter change goes back to the first page, or a narrower result strands the reader on an empty one.
	useEffect(() => setPage(1), [state, syncStatus, withErrors, debouncedSearch])

	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Bundles" />
			<SectionCard title="Filters" description={`${total} matching ${total === 1 ? 'bundle' : 'bundles'}`}>
				<div className="grid items-end gap-4 md:grid-cols-4">
					<div>
						<Label htmlFor="bundle-search">Search</Label>
						<Input
							id="bundle-search"
							value={search}
							placeholder="Title or reference number"
							onChange={(event) => setSearch(event.target.value)}
						/>
					</div>
					<EnumFilter label="State" values={Object.values(WorkfrontBundleState)} value={state} onChange={setState} />
					<EnumFilter
						label="Sync status"
						values={Object.values(WorkfrontBundleSyncStatus)}
						value={syncStatus}
						onChange={setSyncStatus}
					/>
					<Switch isSelected={withErrors} onChange={setWithErrors}>
						With open errors
					</Switch>
				</div>
			</SectionCard>
			<CardShell>
				<BundlesTable
					bundles={query.data?.data ?? []}
					isLoading={query.isLoading}
					isError={query.isError}
					hrefOf={(bundle) => WORKFRONT_PATHS.bundle(bundle.id)}
				/>
				<PaginationFooter
					page={page}
					totalPages={totalPages}
					total={total}
					noun="bundle"
					nounPlural="bundles"
					isFetching={query.isFetching}
					onPage={(next) => setPage(Math.min(Math.max(1, next), totalPages))}
				/>
			</CardShell>
		</div>
	)
}
